/**
 * partLabels – the words a highlighted part is called by.
 *
 * When a teacher highlights a piece of a model, every student sees a tag over
 * it. The tag is only worth drawing if it says something a student can use, and
 * the names inside a GLB are written for a 3D tool, not a classroom:
 * `left_ventricle`, `Aorta.001`, `Object_12`. This module turns whatever naming
 * a part carries into the best human label available, in a fixed order of trust:
 *
 *   1. `meshParts[name].label` — the Scientific Asset Factory's curated label
 *      ("Chain A (protein)", "Saturn ring C"). Written by a person or a
 *      pipeline that knows the science, so it beats anything read off the file.
 *   2. `userData.semanticName` — the factory stamps this on every object it
 *      builds, and glTF carries it through as node extras, so factory assets
 *      are labelled even before `meshParts` is mirrored into `meshy_assets`.
 *   3. `userData.label` — the conventional extras key other tools write.
 *   4. The mesh's own name, cleaned up (see `cleanMeshName`).
 *   5. "Part N" — honest, and consistent with the part's position in the model.
 *
 * Why the parent chain is walked: GLTFLoader puts a node's extras on the
 * object built for the NODE. A node whose mesh has one primitive becomes the
 * Mesh itself, but one with several primitives becomes a Group holding one Mesh
 * per primitive — so the name a picked mesh should wear can sit one level up.
 * The walk stops at the asset root (`assetGroup_*`, a Scene, or a parentless
 * top), because above that is the player's own scaffolding, not the model.
 *
 * Pure: types-only three.js import, no DOM, so node tests can run it directly.
 */

import type * as THREE from 'three';

/**
 * Curated labels keyed by the part's object name, as the factory's `meshParts`
 * list provides them once turned into a lookup.
 */
export type MeshPartLabels = Readonly<Record<string, { label?: string | null } | null | undefined>>;

/**
 * Names that only say "this is a thing". Exporters invent these when the author
 * gave none (`Object_12`, `mesh_0`, `node_3`), and a tag reading "Object 12"
 * is worse than "Part 3": it looks like a name while telling a student nothing.
 * Deliberately short: words like "root" or "group" are real part names in a
 * botany or chemistry lesson, so only exporter vocabulary is listed.
 */
const GENERIC_NAME = /^(?:(?:object|mesh|node|geometry|primitive|scene|untitled)(?:\s*\d+)*|\d+(?:\s+\d+)*)$/i;

/** A string worth showing, or null. */
function textOf(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * A mesh name made readable, or '' when the name carries no meaning.
 *
 *   - A Blender duplicate suffix (`.001`) is dropped: it distinguishes copies
 *     in the editor and means nothing to a student.
 *   - `_` and `-` become spaces.
 *   - Each all-lower-case word gets a capital. Words that already contain a
 *     capital are left alone, so `DNA`, `mRNA` and `H2O` survive intact — a
 *     blanket title-case would print "Dna" in a biology lesson.
 *   - Exporter placeholders (`Object_12`, `mesh_0`) and bare numbers come back
 *     as '' so the caller falls through to "Part N".
 */
export function cleanMeshName(name: string | null | undefined): string {
  const raw = textOf(name);
  if (!raw) return '';
  const words = raw
    .replace(/\.\d+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words || GENERIC_NAME.test(words)) return '';
  return words
    .split(' ')
    .map((word) => (word === word.toLowerCase() ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

/**
 * An authored name, tidied only if it looks like an identifier.
 *
 * The factory writes raw object names into `semanticName` (`left_ventricle`),
 * which want cleaning; a label a person typed ("Sodium ion (Na+)") has spaces
 * in it and is shown exactly as written.
 */
function prettify(value: string): string {
  return /\s/.test(value) ? value : cleanMeshName(value) || value;
}

/** The mesh, then its ancestors, stopping below the asset root. */
function namingChain(mesh: THREE.Object3D): THREE.Object3D[] {
  const chain: THREE.Object3D[] = [mesh];
  for (let node = mesh.parent; node; node = node.parent) {
    const isScene = (node as THREE.Object3D & { isScene?: boolean }).isScene === true;
    if (isScene || !node.parent || node.name.startsWith('assetGroup_')) break;
    chain.push(node);
  }
  return chain;
}

/**
 * The label for one part.
 *
 * `index` is the part's 0-based position in the model — the number after `#`
 * in a modelTools part id — and is shown 1-based, because "Part 0" reads as a
 * bug to anyone who is not a programmer.
 */
export function resolvePartLabel(
  mesh: THREE.Object3D,
  index: number,
  meshParts?: MeshPartLabels | null
): string {
  const chain = namingChain(mesh);

  if (meshParts) {
    for (const node of chain) {
      // GLTFLoader sanitises `name` (dots and spaces go) but keeps the
      // exporter's original on `userData.name`, which is what the factory's
      // `meshParts` were keyed by. Try the original first.
      for (const name of [textOf(node.userData?.name), textOf(node.name)]) {
        if (!name) continue;
        const curated = textOf(meshParts[name]?.label);
        if (curated) return curated;
      }
    }
  }

  for (const key of ['semanticName', 'label'] as const) {
    for (const node of chain) {
      const authored = textOf(node.userData?.[key]);
      if (authored) return prettify(authored);
    }
  }

  // The mesh's own name only: a container's name ("Sketchfab_model", "Heart")
  // would put the same tag on every part underneath it.
  const cleaned = cleanMeshName(textOf(mesh.userData?.name) ?? mesh.name);
  if (cleaned) return cleaned;

  const n = Number.isFinite(index) && index >= 0 ? Math.floor(index) + 1 : 1;
  return `Part ${n}`;
}
